/**
 * Dual-handle price range slider for the price facet.
 *
 * Deliberately decoupled from facets.js: this element mirrors its values into
 * the facet's existing min/max text inputs and dispatches a single synthetic
 * `change` that bubbles to <price-facet-component>, which already listens for
 * change and refreshes the results. Dragging fires `input` (visual only, no
 * network); releasing — or pausing a keyboard arrow-hold — commits once.
 */

/** Quiet period after the last native range `change` before committing. */
const COMMIT_DELAY_MS = 250;

class PriceRangeSlider extends HTMLElement {
  /** Guards against the write-to-input → read-from-input feedback loop. */
  #syncing = false;

  /** @type {HTMLInputElement | null} */
  #minRange = null;
  /** @type {HTMLInputElement | null} */
  #maxRange = null;
  /** @type {HTMLInputElement | null} */
  #minInput = null;
  /** @type {HTMLInputElement | null} */
  #maxInput = null;

  #rangeMax = 0;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #commitTimer = undefined;

  connectedCallback() {
    this.#minRange = this.querySelector('[data-price-slider-min]');
    this.#maxRange = this.querySelector('[data-price-slider-max]');

    const facet = this.closest('price-facet-component');
    this.#minInput = facet?.querySelector('[ref="minInput"]') ?? null;
    this.#maxInput = facet?.querySelector('[ref="maxInput"]') ?? null;

    // Without both pairs there is nothing to keep in sync — leave the text
    // inputs as the sole control rather than half-wiring the slider.
    if (!this.#minRange || !this.#maxRange || !this.#minInput || !this.#maxInput) return;

    this.sync();

    // Section re-renders morph values into the inputs without firing events;
    // re-reading on the next interaction self-heals any stale paint.
    this.addEventListener('pointerdown', this.#onInteract);
    this.addEventListener('focusin', this.#onInteract);
    this.#minRange.addEventListener('input', this.#onRangeInput);
    this.#maxRange.addEventListener('input', this.#onRangeInput);
    // Native change events are intercepted and coalesced: a keyboard
    // arrow-hold fires one change per step, and letting each bubble to
    // <price-facet-component> would trigger a Section Rendering fetch and a
    // history entry per keypress.
    this.#minRange.addEventListener('change', this.#onRangeChange);
    this.#maxRange.addEventListener('change', this.#onRangeChange);
    this.#minInput.addEventListener('input', this.#onTextInput);
    this.#maxInput.addEventListener('input', this.#onTextInput);
  }

  disconnectedCallback() {
    clearTimeout(this.#commitTimer);
    this.removeEventListener('pointerdown', this.#onInteract);
    this.removeEventListener('focusin', this.#onInteract);
    this.#minRange?.removeEventListener('input', this.#onRangeInput);
    this.#maxRange?.removeEventListener('input', this.#onRangeInput);
    this.#minRange?.removeEventListener('change', this.#onRangeChange);
    this.#maxRange?.removeEventListener('change', this.#onRangeChange);
    this.#minInput?.removeEventListener('input', this.#onTextInput);
    this.#maxInput?.removeEventListener('input', this.#onTextInput);
  }

  /**
   * Re-reads the facet's text inputs and repaints the handles. Public so
   * facets.js can call it after clearing a filter (those writes are
   * programmatic and fire no events this element could hear). Range max is
   * re-read each time in case a section re-render changed the bounds.
   */
  sync() {
    this.#rangeMax = Number(this.#maxRange?.max) || 0;
    this.#readFromInputs();
    this.#paint();
  }

  /** Repaint from current input state before the user interacts. */
  #onInteract = () => {
    if (!this.#syncing) this.sync();
  };

  /**
   * @param {Event} event - The range input's input event.
   */
  #onRangeInput = (event) => {
    if (!this.#minRange || !this.#maxRange) return;

    let low = Number(this.#minRange.value);
    let high = Number(this.#maxRange.value);

    // Stop the handles crossing by pushing the one the user isn't dragging.
    if (low > high) {
      if (event.target === this.#minRange) high = low;
      else low = high;
      this.#minRange.value = String(low);
      this.#maxRange.value = String(high);
    }

    this.#paint();
    this.#writeToInputs(low, high);
  };

  /**
   * Coalesces native range `change` bursts into one commit, then re-syncs:
   * the facet may programmatically clamp the text inputs during the commit
   * (no events fire for that), so the handles are re-read afterwards.
   *
   * @param {Event} event - The range input's change event.
   */
  #onRangeChange = (event) => {
    event.stopPropagation();
    clearTimeout(this.#commitTimer);
    this.#commitTimer = setTimeout(() => {
      // Synchronous dispatch: the facet's clamp + update runs during this
      // call, so the inputs hold their final values when it returns.
      this.#minInput?.dispatchEvent(new Event('change', { bubbles: true }));
      this.sync();
    }, COMMIT_DELAY_MS);
  };

  #onTextInput = () => {
    if (this.#syncing) return;
    this.sync();
  };

  /**
   * Pushes slider values into the text inputs. An endpoint sitting at its
   * extreme is written as empty so the filter stays "unset" rather than
   * registering as an active $0–max filter.
   *
   * @param {number} low - Lower handle value.
   * @param {number} high - Upper handle value.
   */
  #writeToInputs(low, high) {
    if (!this.#minInput || !this.#maxInput) return;

    this.#syncing = true;
    // Compare against the floored max: with step="1" a fractional range max
    // (e.g. 49.99) is unreachable, and without the floor a full-right handle
    // would write an active lte filter that excludes the priciest item.
    this.#minInput.value = low <= 0 ? '' : low.toFixed(2);
    this.#maxInput.value = high >= Math.floor(this.#rangeMax) ? '' : high.toFixed(2);
    this.#syncing = false;
  }

  #readFromInputs() {
    if (!this.#minRange || !this.#maxRange || !this.#minInput || !this.#maxInput) return;

    const low = this.#parse(this.#minInput.value);
    const high = this.#parse(this.#maxInput.value);

    this.#minRange.value = String(Number.isFinite(low) ? low : 0);
    this.#maxRange.value = String(Number.isFinite(high) ? high : this.#rangeMax);
  }

  /**
   * Parses a displayed money value, e.g. "3,295.00".
   *
   * Assumes period-decimal formats (the store renders USD). Comma-decimal
   * money formats would need the currency-aware helpers in
   * money-formatting.js before this store could enable them.
   *
   * @param {string} value - The input's displayed value.
   * @returns {number} The parsed amount, or NaN when blank.
   */
  #parse(value) {
    if (typeof value !== 'string' || value.trim() === '') return NaN;
    return Number.parseFloat(value.replace(/[^0-9.]/g, ''));
  }

  /** Drives the filled portion of the track via custom properties. */
  #paint() {
    if (!this.#minRange || !this.#maxRange) return;

    const max = this.#rangeMax || 1;
    const toPercent = (/** @type {number} */ value) => Math.max(0, Math.min(100, (value / max) * 100));

    this.style.setProperty('--price-slider-start', `${toPercent(Number(this.#minRange.value))}%`);
    this.style.setProperty('--price-slider-end', `${toPercent(Number(this.#maxRange.value))}%`);
  }
}

if (!customElements.get('price-range-slider')) {
  customElements.define('price-range-slider', PriceRangeSlider);
}
